import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/siteTranslations';
import SEO from '../components/SEO';

export default function Prices() {
  const { lang } = useLanguage();
  const t = translations[lang].pricing;

  return (
    <div className="space-y-0">
      <SEO
        title={lang === 'da' ? 'Priser – Massage with Jette' : 'Prices & Rates – Massage with Jette'}
        description={lang === 'da'
          ? 'Gennemskuelige priser for massage i København: 60 min (700 DKK), 75 min (860 DKK), 90 min (1.000 DKK), 120 min (1.300 DKK). Spar 15% ved køb af 5 behandlinger. Jette er RAB godkendt - Anerkendt af sundhedsvæsenet.'
          : 'Transparent prices for massage in Copenhagen: 60 min (700 DKK), 75 min (860 DKK), 90 min (1,000 DKK), 120 min (1,300 DKK). Save 15% on 5 sessions. RAB approved therapist.'
        }
      />
      
      {/* Header */}
      <section className="py-16 bg-[#FAF7F2] border-b border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl space-y-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
            {t.tag}
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#2C2825]">
            {t.title}
          </h1>
          <p className="text-base sm:text-lg text-[#6E6760] leading-relaxed">
            {t.subtitle}
          </p>

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[#E8E0D7] text-xs font-semibold text-[#2E3191] shadow-2xs">
            <i className="fa-solid fa-clock text-[#2E3191]" />
            <span>{t.timeBasedNote}</span>
          </div>
        </div>
      </section>

      {/* Main Prices Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          {/* 4 Time-based Pricing Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.times.map((item, idx) => (
              <div
                key={idx}
                className="bg-[#FAF7F2] p-6 sm:p-7 rounded-2xl border border-[#E8E0D7] shadow-xs hover:border-[#2E3191] hover:shadow-md transition-all flex flex-col justify-between space-y-6 text-center group"
              >
                <div className="space-y-3">
                  <span className="text-xs uppercase tracking-wider font-semibold text-[#2E3191] block">
                    {item.duration}
                  </span>
                  <div className="font-serif-display text-3xl sm:text-4xl font-bold text-[#2C2825] group-hover:text-[#2E3191] transition-colors">
                    {item.price}
                  </div>
                  <p className="text-xs sm:text-sm text-[#6E6760] leading-relaxed pt-1">
                    {item.desc}
                  </p>
                </div>

                <a
                  href="https://massage-with-jette.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 rounded-xl bg-[#2E3191] text-white hover:bg-[#1E2063] text-xs sm:text-sm font-semibold transition-all block text-center shadow-xs"
                >
                  {lang === 'da' ? `Book tid (${item.duration})` : `Book session (${item.duration})`}
                </a>
              </div>
            ))}
          </div>

          {/* Special Offers & RAB Certification Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            
            {/* Klippekort - 5 behandlinger (Spar 15%) */}
            <div className="p-7 rounded-2xl bg-[#EEF0F8] border border-[#2E3191]/30 flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#2E3191] bg-white px-2.5 py-1 rounded-md border border-[#2E3191]/20">
                    {t.packageDeal.badge}
                  </span>
                  <i className="fa-solid fa-tags text-xl text-[#2E3191]" />
                </div>
                <h3 className="font-serif-display text-2xl font-bold text-[#2C2825]">
                  {t.packageDeal.title}
                </h3>
                <p className="text-base font-semibold text-[#2E3191]">
                  {t.packageDeal.text}
                </p>
                <p className="text-xs sm:text-sm text-[#6E6760] leading-relaxed">
                  {t.packageDeal.desc}
                </p>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-[#2E3191] text-[#2E3191] text-xs font-semibold hover:bg-[#2E3191] hover:text-white transition-all shadow-2xs"
                >
                  {lang === 'da' ? 'Bestil klippekort / 5 behandlinger' : 'Order 5-session package'} <i className="fa-solid fa-arrow-right text-[10px]" />
                </Link>
              </div>
            </div>

            {/* RAB Godkendt Behandler */}
            <div className="p-7 rounded-2xl bg-[#FAF7F2] border border-[#E8E0D7] flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#2E3191] bg-[#EEF0F8] px-2.5 py-1 rounded-md border border-[#2E3191]/20">
                    {t.rab.badge}
                  </span>
                  <i className="fa-solid fa-certificate text-xl text-[#2E3191]" />
                </div>
                <h3 className="font-serif-display text-2xl font-bold text-[#2C2825]">
                  {t.rab.title}
                </h3>
                <p className="text-base font-semibold text-[#2C2825]">
                  {t.rab.text}
                </p>
                <p className="text-xs sm:text-sm text-[#6E6760] leading-relaxed">
                  {t.rab.desc}
                </p>
              </div>

              <div className="pt-2 text-xs text-[#6E6760] flex items-center gap-2">
                <i className="fa-solid fa-shield-check text-[#2E3191]" />
                <span>{lang === 'da' ? 'Sundhedsfaglig kvalitet, etik og dokumenteret efteruddannelse.' : 'Certified healthcare standards, ethics and accredited training.'}</span>
              </div>
            </div>

          </div>

          {/* Out Call Note */}
          <div className="p-6 rounded-2xl bg-white border border-[#E8E0D7] max-w-3xl mx-auto text-center space-y-2 shadow-2xs">
            <div className="inline-flex items-center gap-2 text-sm font-bold text-[#2C2825]">
              <i className="fa-solid fa-car-side text-[#2E3191]" />
              <span>{lang === 'da' ? 'Udekørende behandlinger' : 'Mobile Out-Call Treatments'}</span>
            </div>
            <p className="text-xs sm:text-sm text-[#6E6760]">
              {t.outCallNote}
            </p>
          </div>

          {/* Payment Options */}
          <div className="pt-8 border-t border-[#E8E0D7] max-w-3xl mx-auto text-center">
            <h3 className="font-serif-display text-2xl font-bold text-[#2C2825] mb-6">
              {t.paymentTitle}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {t.paymentMethods.map((method, idx) => (
                <div
                  key={idx}
                  className="p-5 rounded-xl bg-[#FAF7F2] border border-[#E8E0D7] flex items-center justify-center gap-3 text-sm font-semibold text-[#2C2825]"
                >
                  <i className={`fa-solid ${method.icon} text-lg text-[#2E3191]`} />
                  <span>{method.name}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

    </div>
  );
}
